'use client'

import { useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight, EyeOff, Layers, Link2, LogOut, Moon, Sun, X } from 'lucide-react'
import { Wordmark } from './Wordmark'
import { PlatformMark } from './PlatformMark'
import { iconButtonClass } from './Topbar'
import { isGroup, flatViews, type BrandEntry, type FlatView, type SidebarEntry, type View } from '../dashboardCatalog'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// The sidebar (the design system's .app-sidebar): wordmark, then the three
// headings -- OPERATIONS (Jobs and Actions, Editors only), BRANDS (each with
// its mark), MODERATION and ADMINISTRATION (Editors only) -- and at the
// bottom the workers' health line, the bug report and the profile row with
// Sign out. Items are 34 px, nested lists hang off a 1 px rule, the active
// item is the accent surface with a 2 px primary rule on its left.
//
// Everything the old inline sidebar did is still here: the 48 px rail when
// collapsed (wordmark glyph, expand, theme, bell, bug report), the off-canvas
// drawer below lg with its close button, the "all brands / this brand"
// toggle next to the heading, the shared-template marker (Link2) and the
// hidden-brand marker (EyeOff), ctrl/middle-click on every item (real
// <a href>s), and a brand's own groups ("Default dashboards", "Raw") as
// collapsible nodes, open by default.

// 232 px (plan decision 3). The shell's grid repeats the number as a literal
// Tailwind class (lg:grid-cols-[232px_1fr]): a template string there would
// never be generated.
export const SIDEBAR_WIDTH = 232

export type SidebarProps = {
  theme: ProcessedTheme
  onToggleTheme: () => void
  view: View
  hrefFor: (id: View) => string
  navigate: (id: View) => void
  brands: ReadonlyArray<BrandEntry>
  brandViewsFor: (brand: BrandEntry) => SidebarEntry[]
  /** The platform's registered logo, when it has one (platform.logo_url). */
  logoFor: (brand: BrandEntry) => string | null
  openBrands: Set<string>
  onToggleBrand: (id: string) => void
  activeBrand: BrandEntry | null
  showAllBrands: boolean
  onToggleShowAll: () => void
  hiddenBrands: Set<string>
  isEditor: boolean
  operationsViews: FlatView[]
  moderationViews: SidebarEntry[]
  administrationViews: FlatView[]
  onSignOut: () => void
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  mobileOpen: boolean
  onMobileClose: () => void
  bell: React.ReactNode
  bugReport: React.ReactNode
  bugReportRail: React.ReactNode
  health: { online: number; total: number } | null
  profile: { label: string; detail: string }
}

const HEADING = 'mt-6 mb-2 ml-3 font-mono text-micro font-semibold uppercase tracking-[0.12em] text-subtle'

function itemClass(active: boolean, nested: boolean) {
  // Nested items wrap rather than truncate: three levels of indent leave
  // "Domain Candidates" no room at 232 px, and a cut label is useless.
  const base = `flex w-full items-center gap-2.5 rounded-[2px] pr-2 text-left no-underline transition-colors ${nested ? 'min-h-[30px] py-1 text-small leading-tight' : 'h-[34px] text-nav font-[450]'}`
  return active
    ? `${base} border-l-2 border-primary bg-accent pl-[10px] font-semibold text-primary`
    : `${base} pl-3 text-muted-foreground hover:bg-secondary hover:text-foreground`
}

export function Sidebar(p: SidebarProps) {
  const isDark = p.theme === 'dark'
  // A brand's groups ("Default dashboards") collapse; closed ones are kept here.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set())
  const toggleGroup = (key: string) => setClosedGroups((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  const item = (v: FlatView, nested: boolean) => {
    const active = p.view === v.id
    return (
      <a
        key={v.id}
        href={p.hrefFor(v.id)}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
          e.preventDefault()
          p.navigate(v.id)
        }}
        className={itemClass(active, nested)}
      >
        {nested && <span className={`h-1 w-1 shrink-0 rounded-full ${active ? 'bg-brand-magenta' : 'bg-border'}`} />}
        <span className={nested ? 'min-w-0 break-words' : 'truncate'}>{v.label}</span>
        {v.sharedTemplate && (
          <span title="Shared dashboard template — the same component for every brand, scoped to this one" className="ml-auto shrink-0">
            <Link2 className="h-3 w-3 text-subtle" />
          </span>
        )}
      </a>
    )
  }

  // A flat view plus its children, the children on a rule.
  const itemWithChildren = (v: FlatView, nested: boolean) => (
    <div key={v.id}>
      {item(v, nested)}
      {v.children && v.children.length > 0 && (
        <div className="ml-4 border-l border-border pl-2">
          {v.children.map((c) => item(c, true))}
        </div>
      )}
    </div>
  )

  // A group inside a brand: a collapsible node whose items hang off a rule.
  const groupNode = (g: { group: string; items: SidebarEntry[] }, keyPrefix: string) => {
    const key = `${keyPrefix}/${g.group}`
    const open = !closedGroups.has(key)
    const containsActive = flatViews(g.items).some((v) => v.id === p.view)
    return (
      <div key={key}>
        <button
          type="button"
          onClick={() => toggleGroup(key)}
          className={`flex h-[30px] w-full items-center gap-2 rounded-[2px] pl-3 pr-2 text-left text-small transition-colors hover:text-foreground ${containsActive ? 'text-foreground' : 'text-subtle'}`}
        >
          <ChevronRight className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
          <span className="truncate">{g.group}</span>
        </button>
        {open && (
          <div className="ml-4 border-l border-border pl-2">
            {entries(g.items, key, true)}
          </div>
        )}
      </div>
    )
  }

  const entries = (list: SidebarEntry[], keyPrefix: string, nested: boolean): React.ReactNode =>
    list.map((e) => (isGroup(e) ? groupNode(e, keyPrefix) : itemWithChildren(e, nested)))

  const themeButton = (
    <button type="button" onClick={p.onToggleTheme} title={isDark ? 'Switch to day mode' : 'Switch to night mode'} className={iconButtonClass}>
      {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
    </button>
  )

  const railOnly = p.collapsed && !p.mobileOpen

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-screen flex-col overflow-hidden border-r border-border bg-sidebar-surface transition-transform duration-200 lg:static lg:z-auto lg:w-auto lg:translate-x-0 ${p.mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      style={{ width: railOnly ? undefined : SIDEBAR_WIDTH }}
    >
      {railOnly ? (
        <div className="flex flex-col items-center gap-3 py-5">
          <Wordmark glyphOnly />
          <button type="button" onClick={() => p.onCollapsedChange(false)} title="Expand sidebar" className={`${iconButtonClass} mt-1`}>
            <ChevronRight className="h-4 w-4" />
          </button>
          {themeButton}
          {p.bell}
          {p.bugReportRail}
        </div>
      ) : (
        <>
          <div className="relative flex shrink-0 items-center justify-center px-4 pb-1 pt-6">
            <Wordmark className="min-w-0" />
            {p.mobileOpen ? (
              <button type="button" onClick={p.onMobileClose} title="Close menu" className={`${iconButtonClass} absolute right-2 top-2`}><X className="h-4 w-4" /></button>
            ) : (
              <button type="button" onClick={() => p.onCollapsedChange(true)} title="Collapse sidebar" className={`${iconButtonClass} absolute right-2 top-2 hidden lg:flex`}><ChevronLeft className="h-4 w-4" /></button>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto px-[15px] pb-4">
            {p.operationsViews.length > 0 && (
              <>
                <div className={HEADING}>Operations</div>
                <div className="flex flex-col gap-0.5">
                  {p.operationsViews.map((v) => itemWithChildren(v, false))}
                </div>
              </>
            )}
            <div className="flex items-center justify-between pr-1">
              <div className={HEADING}>Brands</div>
              {p.activeBrand && (
                <button
                  type="button"
                  onClick={p.onToggleShowAll}
                  title={p.showAllBrands ? `Show only ${p.activeBrand.label}` : 'Show all brands'}
                  className={`${iconButtonClass} mt-4 h-6 w-6 ${p.showAllBrands ? 'text-signal' : ''}`}
                >
                  <Layers className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              {p.brands.map((brand) => {
                const isOpen = p.openBrands.has(brand.id)
                const brandViews = p.brandViewsFor(brand)
                const isActiveBrand = flatViews(brand.views).some((v) => v.id === p.view)
                return (
                  <div key={brand.id}>
                    <button
                      type="button"
                      onClick={() => p.onToggleBrand(brand.id)}
                      className={`flex h-[34px] w-full items-center gap-2.5 rounded-[2px] px-2 text-left text-nav font-medium transition-colors hover:bg-secondary ${isActiveBrand ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <PlatformMark code={brand.platformNameCode ?? brand.platformCode} label={brand.label} logoUrl={p.logoFor(brand)} />
                      <span className="truncate">{brand.label}</span>
                      {p.hiddenBrands.has(brand.id) && (
                        <EyeOff className="h-3 w-3 shrink-0 text-subtle" aria-label="Hidden from the public" data-hidden-brand="true" />
                      )}
                      {brandViews.length > 0 && (
                        <ChevronDown className={`ml-auto h-3 w-3 shrink-0 text-subtle transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {isOpen && brandViews.length > 0 && (
                      <div className="mb-1 ml-4 border-l border-border pl-2">
                        {entries(brandViews, brand.id, true)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {p.isEditor && (
              <>
                <div className={HEADING}>Moderation</div>
                <div className="flex flex-col gap-0.5">{entries(p.moderationViews, 'moderation', false)}</div>
                <div className={HEADING}>Administration</div>
                <div className="flex flex-col gap-0.5">{p.administrationViews.map((v) => itemWithChildren(v, false))}</div>
              </>
            )}
          </nav>

          <div className="shrink-0">
            {p.health && (
              <div className="flex items-center gap-2 border-t border-border px-4 py-3 font-mono text-label text-muted-foreground">
                <span className={`h-[6px] w-[6px] shrink-0 rounded-full ${p.health.online > 0 ? 'bg-success' : 'bg-destructive'}`} />
                {p.health.online} of {p.health.total} workers online
              </div>
            )}
            <div className="border-t border-border px-3 py-1">{p.bugReport}</div>
            <div className="flex items-center gap-2.5 border-t border-border px-4 py-3">
              <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full border border-border bg-secondary text-label font-semibold text-muted-foreground">
                {p.profile.label.charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <strong className="block truncate text-nav font-semibold">{p.profile.label}</strong>
                <small className="mt-0.5 block truncate text-label text-muted-foreground">{p.profile.detail}</small>
              </span>
              <button type="button" onClick={p.onSignOut} title="Sign out" className={iconButtonClass}><LogOut className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </>
      )}
    </aside>
  )
}
